import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';

interface ProfileFields {
  operatorCertificate: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  postalCode: string | null;
  addressLine: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  timeZone: string;
  logoDataUrl: string | null;
}

interface OrganizationProfileData {
  legalName: string;
  tradeName: string;
  taxIdentifier: string;
  profile: Partial<ProfileFields>;
  updatedAt: string;
}

const emptyProfile: OrganizationProfileData = {
  legalName: '',
  tradeName: '',
  taxIdentifier: '',
  profile: { timeZone: 'America/Sao_Paulo' },
  updatedAt: '',
};

function field(profile: Partial<ProfileFields>, key: keyof ProfileFields): string {
  const value = profile[key];
  return typeof value === 'string' ? value : '';
}

async function resizeLogo(file: File): Promise<string> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('INVALID_IMAGE_TYPE');
  }
  if (file.size > 5_000_000) throw new Error('IMAGE_TOO_LARGE');
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 512 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('IMAGE_PROCESSING_UNAVAILABLE');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return canvas.toDataURL('image/webp', 0.82);
}

export function OrganizationProfile({
  organizationId,
  onChanged,
  onExpiredSession,
  onboarding = false,
  userName = '',
  userEmail = '',
}: {
  readonly organizationId: string;
  readonly onChanged: () => Promise<void> | void;
  readonly onExpiredSession: () => Promise<void>;
  readonly onboarding?: boolean;
  readonly userName?: string;
  readonly userEmail?: string;
}) {
  const [step, setStep] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const companyLookupAttempt = useRef('');
  const postalLookupAttempt = useRef('');
  const storageKey = `aeronexo-registration-v1:${organizationId}:${userEmail}`;
  const [draft, setDraft] = useState<OrganizationProfileData>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lookingUpPostalCode, setLookingUpPostalCode] = useState(false);
  const [lookingUpCompany, setLookingUpCompany] = useState(false);
  const [error, setError] = useState<string>();
  const [feedback, setFeedback] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/admin/profile`, {
        credentials: 'include',
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      const loaded = (await response.json()) as OrganizationProfileData;
      loaded.profile = {
        ...loaded.profile,
        contactName: loaded.profile.contactName || userName,
        contactEmail: loaded.profile.contactEmail || userEmail,
      };
      try {
        const saved = onboarding
          ? (JSON.parse(sessionStorage.getItem(storageKey) || 'null') as {
              draft: OrganizationProfileData;
              step: number;
            } | null)
          : null;
        if (saved?.draft.updatedAt === loaded.updatedAt) {
          setDraft(saved.draft);
          setStep(Math.max(0, Math.min(2, saved.step)));
        } else setDraft(loaded);
      } catch {
        setDraft(loaded);
      }
      setError(undefined);
    } catch {
      setError('Não foi possível carregar os dados da empresa.');
    } finally {
      setLoading(false);
    }
  }, [onExpiredSession, organizationId, onboarding, storageKey, userName, userEmail]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!loading && onboarding && draft.updatedAt) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ draft, step }));
      } catch {
        /* Storage may be disabled. The form remains usable. */
      }
    }
  }, [draft, loading, onboarding, step, storageKey]);

  useEffect(() => {
    heading.current?.focus();
  }, [step]);

  const changeProfile = (key: keyof ProfileFields, value: string | null) => {
    setDraft((current) => ({
      ...current,
      profile: { ...current.profile, [key]: value },
    }));
  };

  const lookupPostalCode = async () => {
    if (lookingUpPostalCode) return;
    const postalCode = field(draft.profile, 'postalCode').replace(/\D/g, '');
    if (postalCode.length !== 8) {
      setError('Informe um CEP com oito números.');
      return;
    }
    setLookingUpPostalCode(true);
    postalLookupAttempt.current = postalCode;
    setError(undefined);
    try {
      const response = await fetch(`/v1/reference/postal-code/${postalCode}`, {
        credentials: 'include',
        signal: AbortSignal.timeout(12000),
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      const body = (await response.json()) as {
        result: null | {
          postalCode: string;
          addressLine: string;
          addressComplement: string;
          district: string;
          city: string;
          state: string;
        };
      };
      if (!body.result) {
        setError('CEP não encontrado. Você pode preencher o endereço manualmente.');
        return;
      }
      setDraft((current) =>
        field(current.profile, 'postalCode').replace(/\D/g, '') !== postalCode
          ? current
          : {
              ...current,
              profile: {
                ...current.profile,
                postalCode: body.result!.postalCode,
                addressLine: body.result!.addressLine || current.profile.addressLine || null,
                addressComplement:
                  current.profile.addressComplement || body.result!.addressComplement || null,
                district: body.result!.district || current.profile.district || null,
                city: body.result!.city || current.profile.city || null,
                state: body.result!.state || current.profile.state || null,
              },
            },
      );
      setFeedback('Endereço sugerido pelo ViaCEP. Confira antes de salvar.');
    } catch {
      setError('A consulta de CEP está indisponível. Continue preenchendo manualmente.');
    } finally {
      setLookingUpPostalCode(false);
    }
  };

  const lookupCompany = async () => {
    if (lookingUpCompany) return;
    const taxIdentifier = draft.taxIdentifier.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
    if (!/^\d{14}$/.test(taxIdentifier)) {
      setError('A busca automática está disponível para CNPJ numérico com 14 posições.');
      return;
    }
    setLookingUpCompany(true);
    companyLookupAttempt.current = taxIdentifier;
    setError(undefined);
    try {
      const response = await fetch(`/v1/reference/company/${taxIdentifier}`, {
        credentials: 'include',
        signal: AbortSignal.timeout(12000),
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) throw new Error(String(response.status));
      const body = (await response.json()) as {
        result: null | {
          legalName: string;
          tradeName: string;
          postalCode: string;
          addressLine: string;
          addressNumber: string;
          addressComplement: string;
          district: string;
          city: string;
          state: string;
          contactEmail: string;
          contactPhone: string;
          registryStatus: string;
        };
      };
      if (!body.result) {
        setError('CNPJ não encontrado. Continue preenchendo manualmente.');
        return;
      }
      const result = body.result;
      setDraft((current) =>
        current.taxIdentifier.replace(/\D/g, '') === taxIdentifier
          ? {
              ...current,
              legalName: result.legalName || current.legalName,
              tradeName: result.tradeName || result.legalName || current.tradeName,
              profile: {
                ...current.profile,
                postalCode: result.postalCode || current.profile.postalCode || null,
                addressLine: result.addressLine || current.profile.addressLine || null,
                addressNumber: result.addressNumber || current.profile.addressNumber || null,
                addressComplement:
                  current.profile.addressComplement || result.addressComplement || null,
                district: result.district || current.profile.district || null,
                city: result.city || current.profile.city || null,
                state: result.state || current.profile.state || null,
                contactEmail: current.profile.contactEmail || result.contactEmail || null,
                contactPhone: current.profile.contactPhone || result.contactPhone || null,
              },
            }
          : current,
      );
      setFeedback(
        `Dados públicos sugeridos${result.registryStatus ? ` · situação informada pela fonte: ${result.registryStatus}` : ''}. Confira antes de salvar.`,
      );
    } catch {
      setError('A consulta de CNPJ está indisponível. Continue preenchendo manualmente.');
    } finally {
      setLookingUpCompany(false);
    }
  };

  const chooseLogo = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError(undefined);
    try {
      changeProfile('logoDataUrl', await resizeLogo(file));
      setFeedback('Logomarca preparada. Salve os dados para confirmar.');
    } catch (imageError) {
      setError(
        imageError instanceof Error && imageError.message === 'IMAGE_TOO_LARGE'
          ? 'A imagem deve ter no máximo 5 MB.'
          : 'Use uma imagem PNG, JPG ou WebP válida.',
      );
    } finally {
      event.target.value = '';
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      step === 0 &&
      !/^[A-Z0-9]{12}\d{2}$/.test(draft.taxIdentifier.replace(/[^0-9A-Za-z]/g, '').toUpperCase())
    ) {
      setError('Confira o CNPJ: são 14 posições, com dois dígitos finais.');
      return;
    }
    if (
      step === 1 &&
      (!/^[^@]{3,}$/.test(field(draft.profile, 'contactName').trim()) ||
        !/^\+?[\d\s().-]+$/.test(field(draft.profile, 'contactPhone')) ||
        field(draft.profile, 'contactPhone').replace(/\D/g, '').length < 10 ||
        field(draft.profile, 'contactPhone').replace(/\D/g, '').length > 15)
    ) {
      setError('Confira o nome do responsável e o telefone com DDD.');
      return;
    }
    if (step < 2) {
      setStep(step + 1);
      setError(undefined);
      setFeedback(undefined);
      return;
    }
    setSaving(true);
    setError(undefined);
    setFeedback(undefined);
    try {
      const response = await fetch(`/v1/organizations/${organizationId}/admin/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          profile: {
            ...draft.profile,
            timeZone: draft.profile.timeZone || 'America/Sao_Paulo',
          },
        }),
      });
      if (response.status === 401) return onExpiredSession();
      if (!response.ok) {
        if (response.status === 400) {
          const result = (await response.json().catch(() => null)) as { fields?: string[] } | null;
          const first = result?.fields?.[0] || '';
          const contact = ['profile.contactName', 'profile.contactEmail', 'profile.contactPhone'];
          setStep(
            contact.includes(first)
              ? 1
              : first.startsWith('profile.') &&
                  first !== 'profile.logoDataUrl' &&
                  first !== 'profile.operatorCertificate'
                ? 2
                : 0,
          );
        }
        setError(
          response.status === 409
            ? 'Este CNPJ já está vinculado a outra organização.'
            : 'Revise os campos indicados. Seus dados continuam no formulário.',
        );
        return;
      }
      setDraft((await response.json()) as OrganizationProfileData);
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* Storage may be disabled. */
      }
      setFeedback('Dados da empresa salvos.');
      await onChanged();
    } catch {
      setError('Não foi possível salvar. Seus dados continuam no formulário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="organizationProfilePanel" aria-labelledby="organization-profile-title">
      <div className="resourcePanelHeading">
        <div>
          <span className="sectionEyebrow">
            {onboarding ? 'Bem-vindo · Cadastro inicial' : 'Cadastro da empresa'}
          </span>
          <h2 ref={heading} tabIndex={-1} id="organization-profile-title">
            {['Sua empresa', 'Quem responde pela empresa', 'Onde sua empresa está'][step]}
          </h2>
          <p>
            {
              [
                'Confira os dados do convite. Use o CNPJ para buscar o endereço e os dados públicos.',
                'Já aproveitamos os dados da sua conta. Confira o contato da empresa.',
                'Confira a localização. Use o CEP para preencher o endereço.',
              ][step]
            }
          </p>
        </div>
        {draft.profile.logoDataUrl ? (
          <img
            className="organizationLogoPreview"
            src={draft.profile.logoDataUrl}
            alt={`Logomarca de ${draft.tradeName}`}
          />
        ) : (
          <span className="organizationLogoFallback" aria-hidden="true">
            {draft.tradeName.slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>
      <ol className="registrationSteps" aria-label="Etapas do cadastro">
        {['Empresa', 'Responsável', 'Localização'].map((label, index) => (
          <li key={label} aria-current={step === index ? 'step' : undefined}>
            <span>{index < step ? '✓' : index + 1}</span>
            {label}
          </li>
        ))}
      </ol>
      <p className="registrationHint">
        <span className="requiredMark" aria-hidden="true">
          *
        </span>{' '}
        Campos obrigatórios.{' '}
        {onboarding
          ? 'Depois dos dados da empresa, cadastre a base e sua primeira aeronave para acessar a operação.'
          : ''}
      </p>
      {loading ? <p role="status">Carregando dados da empresa…</p> : null}
      {error ? (
        <p className="inlineError" role="alert">
          {error}
        </p>
      ) : null}
      {!loading && !draft.updatedAt ? (
        <button type="button" onClick={() => void load()}>
          Tentar carregar novamente
        </button>
      ) : null}
      {!loading ? (
        <form className="profileForm" onSubmit={(event) => void submit(event)}>
          <fieldset
            className="registrationFields"
            hidden={step !== 0}
            disabled={step !== 0 || saving || lookingUpCompany}
          >
            <legend className="srOnly">Empresa</legend>
            <div className="resourceFormGrid">
              <label>
                Razão social{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <input
                  required
                  minLength={2}
                  autoComplete="organization"
                  value={draft.legalName}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, legalName: event.target.value }))
                  }
                />
              </label>
              <label>
                Nome usado no sistema{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <input
                  required
                  minLength={2}
                  value={draft.tradeName}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, tradeName: event.target.value }))
                  }
                />
              </label>
              <label>
                CNPJ{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <span className="lookupFieldRow">
                  <input
                    required
                    maxLength={18}
                    aria-label="CNPJ"
                    minLength={14}
                    onBlur={() => {
                      if (
                        /^\d{14}$/.test(draft.taxIdentifier.replace(/[^0-9A-Za-z]/g, '')) &&
                        companyLookupAttempt.current !==
                          draft.taxIdentifier.replace(/[^0-9A-Za-z]/g, '')
                      )
                        void lookupCompany();
                    }}
                    value={draft.taxIdentifier}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        taxIdentifier: event.target.value.toUpperCase(),
                      }))
                    }
                  />
                  <button
                    className="secondaryButton"
                    disabled={lookingUpCompany}
                    type="button"
                    onClick={() => void lookupCompany()}
                  >
                    {lookingUpCompany ? 'Buscando…' : 'Buscar dados'}
                  </button>
                </span>
              </label>
              <label>
                Certificado do operador aéreo
                <input
                  maxLength={80}
                  placeholder="Informe como aparece no certificado"
                  value={field(draft.profile, 'operatorCertificate')}
                  onChange={(event) => changeProfile('operatorCertificate', event.target.value)}
                />
              </label>
            </div>
            <label className="logoUpload">
              Logomarca
              <input
                accept="image/png,image/jpeg,image/webp"
                type="file"
                onChange={(event) => void chooseLogo(event)}
              />
              <small>PNG, JPG ou WebP, até 5 MB.</small>
            </label>
          </fieldset>
          <fieldset
            className="registrationFields"
            hidden={step !== 1}
            disabled={step !== 1 || saving}
          >
            <legend className="srOnly">Responsável</legend>
            <div className="resourceFormGrid">
              <label>
                Responsável por este cadastro{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <input
                  autoComplete="name"
                  required
                  minLength={3}
                  maxLength={160}
                  pattern="[^@]+"
                  value={field(draft.profile, 'contactName')}
                  onChange={(event) => changeProfile('contactName', event.target.value)}
                />
              </label>
              <label>
                E-mail de contato{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <input
                  autoComplete="email"
                  type="email"
                  required
                  value={field(draft.profile, 'contactEmail')}
                  onChange={(event) => changeProfile('contactEmail', event.target.value)}
                />
              </label>
              <label>
                Telefone{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <input
                  autoComplete="tel"
                  inputMode="tel"
                  required
                  minLength={10}
                  maxLength={32}
                  value={field(draft.profile, 'contactPhone')}
                  onChange={(event) => changeProfile('contactPhone', event.target.value)}
                />
              </label>
            </div>
          </fieldset>
          <fieldset
            className="registrationFields"
            hidden={step !== 2}
            disabled={step !== 2 || saving || lookingUpPostalCode}
          >
            <legend className="srOnly">Localização</legend>
            <div className="resourceFormGrid">
              <label className="lookupField">
                CEP
                <span>
                  <input
                    autoComplete="postal-code"
                    aria-label="CEP"
                    inputMode="numeric"
                    maxLength={9}
                    onBlur={() => {
                      if (
                        field(draft.profile, 'postalCode').replace(/\D/g, '').length === 8 &&
                        postalLookupAttempt.current !==
                          field(draft.profile, 'postalCode').replace(/\D/g, '')
                      )
                        void lookupPostalCode();
                    }}
                    value={field(draft.profile, 'postalCode')}
                    onChange={(event) => changeProfile('postalCode', event.target.value)}
                  />
                  <button
                    className="secondaryButton"
                    disabled={lookingUpPostalCode}
                    type="button"
                    onClick={() => void lookupPostalCode()}
                  >
                    {lookingUpPostalCode ? 'Buscando…' : 'Buscar CEP'}
                  </button>
                </span>
              </label>
              <label>
                Cidade{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <input
                  value={field(draft.profile, 'city')}
                  required
                  minLength={2}
                  maxLength={120}
                  autoComplete="address-level2"
                  onChange={(event) => changeProfile('city', event.target.value)}
                />
              </label>
              <label>
                UF{' '}
                <span className="requiredMark" aria-hidden="true">
                  *
                </span>
                <select
                  required
                  autoComplete="address-level1"
                  value={field(draft.profile, 'state')}
                  onChange={(event) => changeProfile('state', event.target.value)}
                >
                  <option value="">Selecione</option>
                  {'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'
                    .split(' ')
                    .map((uf) => (
                      <option key={uf}>{uf}</option>
                    ))}
                </select>
              </label>
            </div>
            <details className="registrationAddressDetails">
              <summary>Detalhes do endereço</summary>
              <div className="resourceFormGrid">
                <label>
                  Endereço
                  <input
                    autoComplete="street-address"
                    value={field(draft.profile, 'addressLine')}
                    onChange={(event) => changeProfile('addressLine', event.target.value)}
                  />
                </label>
                <label>
                  Número
                  <input
                    value={field(draft.profile, 'addressNumber')}
                    onChange={(event) => changeProfile('addressNumber', event.target.value)}
                  />
                </label>
                <label>
                  Complemento
                  <input
                    value={field(draft.profile, 'addressComplement')}
                    onChange={(event) => changeProfile('addressComplement', event.target.value)}
                  />
                </label>
                <label>
                  Bairro
                  <input
                    value={field(draft.profile, 'district')}
                    onChange={(event) => changeProfile('district', event.target.value)}
                  />
                </label>
              </div>
            </details>
          </fieldset>

          <div className="registrationActions">
            {step > 0 ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setStep(step - 1);
                  setError(undefined);
                }}
              >
                Voltar
              </button>
            ) : null}
            <span>
              Etapa {step + 1} de {onboarding ? 5 : 3}
            </span>
            <button
              className="primaryButton"
              disabled={
                saving || loading || !draft.updatedAt || lookingUpCompany || lookingUpPostalCode
              }
              type="submit"
            >
              {saving
                ? 'Salvando cadastro…'
                : step < 2
                  ? 'Continuar'
                  : onboarding
                    ? 'Salvar dados e cadastrar a base'
                    : 'Salvar cadastro'}
            </button>
          </div>
          {feedback ? (
            <p className="invitationFeedback invitationFeedback-success" role="status">
              {feedback}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
